import type { ReactNode } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ToWords } from "to-words/en-IN";
import AuthlessPrintChrome from "@/components/common-receipt/AuthlessPrintChrome";
import { useAuthlessPrintRow } from "@/components/common-receipt/use-authless-print-row";
import ReceiptPrint from "@/components/receipt-layout";
import { useHardcopyBorders } from "@/theme/useHardcopyBorders";
import { isYearlySabilType } from "@/constants";
import { formatListDate } from "@/utils/date-format";

dayjs.extend(utc);

const formatMonthlyPeriod = (
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined
) => ({
  periodStart: periodStart ? formatListDate(periodStart, { utc: true }) : null,
  periodEnd: periodEnd ? formatListDate(periodEnd, { utc: true }) : null,
});

const formatYearlyPeriod = (
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined
) => {
  if (!periodStart) {
    return {
      periodStart: null,
      periodEnd: null,
    };
  }

  const startDate = dayjs.utc(periodStart);
  const endDate = periodEnd ? dayjs.utc(periodEnd) : null;

  if (startDate.month() === 3 && endDate && endDate.month() === 2) {
    const startYear = startDate.year();
    const endYear = endDate.year();
    return {
      periodStart: `April ${startYear}`,
      periodEnd: `March ${endYear}`,
    };
  }

  return {
    periodStart: periodStart ? formatListDate(periodStart, { utc: true }) : null,
    periodEnd: periodEnd ? formatListDate(periodEnd, { utc: true }) : null,
  };
};

type ReceiptApiRow = {
  amount?: number;
  remarks?: string;
  receiptDate?: string;
  receiptNo?: string;
  paymentMode?: string;
  takhmeenAmount?: number | null;
  paidMonths?: number | null;
  periodStart?: string;
  periodEnd?: string;
  sabilData?: {
    sabilNo?: string;
    sabilType?: string;
    itsNo?: string;
    name?: string;
    firmName?: string;
    firmAddress?: string;
    address?: string;
    itsdata?: { ITS_ID?: string; HOF_ID?: string | null; HOF_FM_TYPE?: string | null };
  };
};

/** Same source as SabilReceiptList “HOF ITS” column: sabil.itsNo, then directory HOF/ITS. */
function resolveHofItsForPrint(sabilData: ReceiptApiRow["sabilData"]): string {
  const itsNo = sabilData?.itsNo?.trim();
  if (itsNo) return itsNo;

  const its = sabilData?.itsdata;
  if (!its) return "";

  if (its.HOF_FM_TYPE === "FM" && its.HOF_ID?.trim()) {
    return its.HOF_ID.trim();
  }
  return its.ITS_ID?.trim() || its.HOF_ID?.trim() || "";
}

const SabilReceiptPrint = () => {
  const { solid1, solid1Soft, solid5 } = useHardcopyBorders();
  const { data, error } = useAuthlessPrintRow<ReceiptApiRow>({
    location: "sabilReceipt",
    idFromSearchParam: "receiptId",
  });

  return (
    <AuthlessPrintChrome data={data} error={error}>
      {(receiptData) => {
        const sabilData = receiptData.sabilData || {};
        const hofIts = resolveHofItsForPrint(sabilData);
        const sabilType = sabilData?.sabilType;
        const isYearlySabil = isYearlySabilType(sabilType);
        const isEstablishment = sabilType === "ESTABLISHMENT";
        const displayName = isEstablishment
          ? sabilData?.firmName || sabilData?.name
          : sabilData?.name;
        const displayAddress = isEstablishment
          ? sabilData?.firmAddress || sabilData?.address
          : sabilData?.address;

        const period = isYearlySabil
          ? formatYearlyPeriod(receiptData.periodStart, receiptData.periodEnd)
          : formatMonthlyPeriod(receiptData.periodStart, receiptData.periodEnd);
        const { periodStart, periodEnd } = period;

        const toWords = new ToWords();

        const LabelValue = ({
          label,
          value,
          noBorder,
        }: {
          label: ReactNode;
          value: ReactNode;
          noBorder?: boolean;
        }) => (
          <div style={{ borderBottom: !noBorder ? solid1 : undefined }}>
            <div style={{ textAlign: "right", padding: "10px" }}>{label}</div>
            <div style={{ fontSize: "13px", textAlign: "right", padding: "10px" }}>{value}</div>
          </div>
        );

        return (
          <ReceiptPrint>
            <div style={{ display: "flex", width: "100%" }}>
              <div
                className="u-col u-col-82p27"
                style={{ boxSizing: "border-box", padding: "0", borderTop: solid5 }}
              >
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ paddingRight: "10px" }}>نام</div>
                    <div style={{ flex: "3", borderBottom: solid1Soft }}>{displayName}</div>
                    <div style={{ paddingLeft: "10px" }}>حفظ الله تعالى</div>
                  </div>
                </div>
                <div style={{ padding: "10px", borderBottom: solid1 }}>{displayAddress}</div>

                <div style={{ textAlign: "center", padding: "10px" }}>بعد السلام الجميل</div>
                <div style={{ padding: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ flex: "3", paddingRight: "10px", borderBottom: solid1Soft }}>
                      {toWords.convert(receiptData.amount || 0)} Only
                    </div>
                    <div style={{ flex: "0.5", paddingRight: "10px", textAlign: "center" }}>
                      انكه
                    </div>
                    <div style={{ flex: "1", borderBottom: solid1Soft }}>{receiptData.amount}</div>
                    <div style={{ flex: "2", paddingLeft: "10px", textAlign: "right" }}>
                      اٰپ طرف سي روپية
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", padding: "10px" }}>
                  <strong>سبيل الخير والبركات</strong>&rdquo; فند ما&ldquo;
                </div>

                <div style={{ padding: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ flex: "3", paddingRight: "10px" }}>سنة عيسوي وصول تهيا چهے</div>
                    {periodEnd && (
                      <>
                        <div style={{ flex: "1.5", borderBottom: solid1Soft }}>{periodEnd}</div>
                        <div style={{ flex: "1", textAlign: "center" }}>إلى</div>
                      </>
                    )}
                    {periodStart && (
                      <div style={{ flex: isYearlySabil ? "3" : "1.5", borderBottom: solid1Soft }}>
                        {periodStart}
                      </div>
                    )}
                    <div style={{ flex: "1.2", paddingLeft: "10px", textAlign: "right" }}>
                      من شهر
                    </div>
                  </div>
                </div>

                {receiptData.remarks ? (
                  <div
                    style={{
                      padding: "20px 10px",
                      borderTop: solid1,
                      display: "flex",
                      alignItems: "baseline",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap", marginRight: "8px" }}>Remarks:</span>
                    <span style={{ flex: 1, borderBottom: solid1Soft, minWidth: 0 }}>
                      {receiptData.remarks}
                    </span>
                  </div>
                ) : null}
              </div>

              <div
                className="u-col u-col-17p73"
                style={{
                  boxSizing: "border-box",
                  borderTop: solid5,
                  borderLeft: solid5,
                }}
              >
                <LabelValue label="تاريخ" value={formatListDate(receiptData.receiptDate ?? "")} />
                <LabelValue label="رسيد نمبر" value={receiptData.receiptNo} />
                <LabelValue label="سبيل نمبر" value={sabilData.sabilNo} />
                <LabelValue label="Sabil Type" value={sabilType || "—"} />
                <LabelValue label="HOF ITS" value={hofIts || "—"} noBorder />
              </div>
            </div>
          </ReceiptPrint>
        );
      }}
    </AuthlessPrintChrome>
  );
};

export default SabilReceiptPrint;
